import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import BusinessCard from "@/components/BusinessCard";
import BusinessMap from "@/components/BusinessMap";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search, Map as MapIcon, List, SlidersHorizontal } from "lucide-react";

export default function Catalogue() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ sectors: [], regions: [] });
  const [sector, setSector] = useState("all");
  const [region, setRegion] = useState("all");
  const [minProf, setMinProf] = useState(0);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/meta/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (sector !== "all") params.sector = sector;
    if (region !== "all") params.region = region;
    if (minProf > 0) params.min_profitability = minProf;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/businesses", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [sector, region, minProf, search]);

  const reset = () => { setSector("all"); setRegion("all"); setMinProf(0); setSearch(""); };

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFDFC]">
      <Header />

      {/* Filter bar */}
      <div className="border-b border-gray-200 bg-white sticky top-[68px] z-40">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-4">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
              <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (ville, secteur, nom...)" className="rounded-none pl-9 border-gray-300" />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="filter-sector" className="rounded-none w-[180px] border-gray-300"><SelectValue placeholder="Secteur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les secteurs</SelectItem>
                  {filters.sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="filter-region" className="rounded-none w-[200px] border-gray-300"><SelectValue placeholder="Région" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les régions</SelectItem>
                  {filters.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-3 border border-gray-300 px-4 min-w-[210px]">
                <SlidersHorizontal className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} />
                <span className="text-xs text-[#475569] whitespace-nowrap">Rentab. ≥ {minProf}%</span>
                <Slider data-testid="filter-profitability" value={[minProf]} onValueChange={(v) => setMinProf(v[0])} max={35} step={1} className="w-24" />
              </div>
              <Button data-testid="reset-filters" variant="outline" className="rounded-full border-gray-300" onClick={reset}>Réinitialiser</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile toggle */}
      <div className="lg:hidden flex border-b border-gray-200 bg-white">
        <button data-testid="mobile-view-list" onClick={() => setMobileView("list")} className={`flex-1 py-3 text-sm flex items-center justify-center gap-2 ${mobileView === "list" ? "text-[#1B2A4A] border-b-2 border-[#E0C173]" : "text-gray-400"}`}><List className="w-4 h-4" /> Liste</button>
        <button data-testid="mobile-view-map" onClick={() => setMobileView("map")} className={`flex-1 py-3 text-sm flex items-center justify-center gap-2 ${mobileView === "map" ? "text-[#1B2A4A] border-b-2 border-[#E0C173]" : "text-gray-400"}`}><MapIcon className="w-4 h-4" /> Carte</button>
      </div>

      {/* Split view */}
      <div className="flex-1 flex flex-col lg:flex-row max-w-[1600px] w-full mx-auto">
        {/* List */}
        <div className={`${mobileView === "map" ? "hidden" : "block"} lg:block lg:w-1/2 px-5 sm:px-8 py-8 overflow-y-auto scrollbar-thin`} style={{ maxHeight: "calc(100vh - 130px)" }}>
          <p data-testid="results-count" className="text-sm text-[#475569] mb-6">{loading ? "Chargement…" : `${items.length} entreprise${items.length > 1 ? "s" : ""} à céder`}</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {items.map((b, i) => <BusinessCard key={b.business_id} b={b} index={i} active={activeId === b.business_id} onHover={setActiveId} />)}
          </div>
          {!loading && items.length === 0 && <p className="text-center text-gray-400 py-20">Aucune entreprise ne correspond à vos critères.</p>}
        </div>
        {/* Map */}
        <div className={`${mobileView === "list" ? "hidden" : "block"} lg:block lg:w-1/2 lg:sticky lg:top-[130px]`} style={{ height: "calc(100vh - 130px)" }}>
          <BusinessMap items={items} activeId={activeId} onHover={setActiveId} />
        </div>
      </div>
    </div>
  );
}
